'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { submitForm, FORMSUBMIT_EMAIL, GOOGLE_SHEETS_URL, openWhatsAppFallback } from '@/lib/formSubmit';

interface FormData {
  name: string;
  email: string;
  phone: string;
  projectType: string;
  budget: string;
  message: string;
}

const ConsultationCTA = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    budget: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string>('');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    if (!isHydrated) return;
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isHydrated) return;

    setIsSubmitting(true);
    setSubmitError('');

    // Submit to FormSubmit and Google Sheets
    const result = await submitForm(formData, FORMSUBMIT_EMAIL, GOOGLE_SHEETS_URL);
    
    setIsSubmitting(false);

    if (result.success) {
      setSubmitSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        projectType: '',
        budget: '',
        message: ''
      });

      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    } else {
      setSubmitError(result.error || 'Failed to submit form. Please try again.');
      
      // Fallback: Open WhatsApp with the form details
      setTimeout(() => {
        openWhatsAppFallback(formData);
      }, 1500);
    }
  };

  if (!isHydrated) {
    return (
      <section className="py-20 bg-background consultation">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="bg-card rounded-lg p-12 shadow-strong">
            <h2 className="font-headline text-4xl font-bold text-foreground mb-4">
              Book Your Free Consultation
            </h2>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-black relative" id="consultation" style={{ isolation: 'isolate' }}>
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-20 transform-gpu">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16">
          {/* Left Side - Content */}
          <div>
            <div className="inline-flex items-center gap-2.5 px-4 md:px-5 py-2 md:py-2.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-4 md:mb-6 will-change-transform">
              <div className="w-1.5 h-1.5 bg-white rounded-full" />
              <span className="text-xs font-medium text-white/90 tracking-widest uppercase">
                Get Started
              </span>
            </div>
            <h2 className="font-headline text-3xl md:text-4xl lg:text-6xl font-normal text-white mb-6 md:mb-8 tracking-tight leading-tight">
              Let's Create Your
              <span className="block font-normal mt-1 md:mt-2">Dream Space</span>
            </h2>
            <p className="text-base md:text-lg text-white/70 mb-8 md:mb-12 font-normal leading-relaxed">
              Book a complimentary consultation with our design experts. We'll discuss your vision, timeline, and budget to create a tailored approach for your project.
            </p>

            <div className="space-y-6 md:space-y-8">
              <div className="flex items-start gap-3 md:gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-white/5 border border-white/10 rounded-full flex items-center justify-center flex-shrink-0 transform-gpu">
                  <Icon name="CalendarIcon" size={18} className="text-white md:w-5 md:h-5" />
                </div>
                <div>
                  <h3 className="font-headline text-lg md:text-xl font-normal text-white mb-1 md:mb-2">Flexible Scheduling</h3>
                  <p className="text-white/60 font-normal text-xs md:text-sm leading-relaxed">
                    In-person or virtual consultations at your convenience
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 md:gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-white/5 border border-white/10 rounded-full flex items-center justify-center flex-shrink-0 transform-gpu">
                  <Icon name="UserGroupIcon" size={18} className="text-white md:w-5 md:h-5" />
                </div>
                <div>
                  <h3 className="font-headline text-lg md:text-xl font-normal text-white mb-1 md:mb-2">Expert Guidance</h3>
                  <p className="text-white/60 font-normal text-xs md:text-sm leading-relaxed">
                    Senior designers with deep understanding of Indian living
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 md:gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-white/5 border border-white/10 rounded-full flex items-center justify-center flex-shrink-0 transform-gpu">
                  <Icon name="DocumentTextIcon" size={18} className="text-white md:w-5 md:h-5" />
                </div>
                <div>
                  <h3 className="font-headline text-lg md:text-xl font-normal text-white mb-1 md:mb-2">Detailed Proposal</h3>
                  <p className="text-white/60 font-normal text-xs md:text-sm leading-relaxed">
                    Comprehensive project roadmap with timeline and estimates
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 md:mt-12 pt-6 md:pt-8 border-t border-white/10">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 md:gap-4">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Icon key={i} name="StarIcon" size={16} className="text-white md:w-[18px] md:h-[18px]" variant="solid" />
                  ))}
                </div>
                <span className="text-white/70 text-xs md:text-sm font-normal">4.9/5 from 100+ homeowners</span>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 md:p-8 lg:p-12 will-change-transform transform-gpu">
            {submitSuccess ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-12">
                <div className="w-20 h-20 bg-white/10 border border-white/20 rounded-full flex items-center justify-center mb-6 transform-gpu">
                  <Icon name="CheckCircleIcon" size={48} className="text-white" />
                </div>
                <h3 className="font-headline text-3xl font-normal text-white mb-4">
                  Thank You!
                </h3>
                <p className="text-white/70 font-normal mb-8 leading-relaxed">
                  We've received your request. Our team will contact you within 24 hours.
                </p>
                <button
                  onClick={() => setSubmitSuccess(false)}
                  className="px-8 py-4 bg-white text-black font-medium rounded-full transition-all duration-500 hover:scale-105"
                >
                  Submit Another Request
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5 md:space-y-6">
                <div>
                  <label htmlFor="name" className="block text-xs md:text-sm font-medium text-white/90 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 md:py-3.5 bg-white/5 border border-white/10 rounded-xl text-sm md:text-base text-white placeholder:text-white/40 focus:outline-none focus:border-white/30 transition-colors duration-200 transform-gpu"
                    placeholder="Enter your full name"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-white/90 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/40 focus:outline-none focus:border-white/30 transition-colors duration-200 transform-gpu"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-white/90 mb-2">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/40 focus:outline-none focus:border-white/30 transition-colors duration-200 transform-gpu"
                      placeholder="+91 98862 76722"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="projectType" className="block text-sm font-medium text-white/90 mb-2">
                      Project Type *
                    </label>
                    <select
                      id="projectType"
                      name="projectType"
                      value={formData.projectType}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-white/30 transition-colors duration-200 transform-gpu"
                    >
                      <option value="" className="bg-black">Select type</option>
                      <option value="apartment" className="bg-black">Apartment</option>
                      <option value="villa" className="bg-black">Villa</option>
                      <option value="house" className="bg-black">House</option>
                      <option value="commercial" className="bg-black">Commercial</option>
                      <option value="renovation" className="bg-black">Renovation</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="budget" className="block text-sm font-medium text-white/90 mb-2">
                      Estimated Budget *
                    </label>
                    <select
                      id="budget"
                      name="budget"
                      value={formData.budget}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-white/30 transition-colors duration-200 transform-gpu"
                    >
                      <option value="" className="bg-black">Select range</option>
                      <option value="10-20" className="bg-black">₹10-20 Lakhs</option>
                      <option value="20-35" className="bg-black">₹20-35 Lakhs</option>
                      <option value="35-50" className="bg-black">₹35-50 Lakhs</option>
                      <option value="50+" className="bg-black">₹50+ Lakhs</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-white/90 mb-2">
                    Project Details
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/40 focus:outline-none focus:border-white/30 transition-colors duration-200 resize-none transform-gpu"
                    placeholder="Tell us about your vision..."
                  />
                </div>

                {submitError && (
                  <div className="bg-error/10 border border-error/20 rounded-lg p-4 flex items-start gap-3">
                    <Icon name="ExclamationCircleIcon" size={20} className="text-error flex-shrink-0 mt-0.5" variant="solid" />
                    <div>
                      <p className="text-sm text-error font-semibold mb-1">Submission Failed</p>
                      <p className="text-sm text-error/80">{submitError}</p>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-8 py-4 bg-white text-black font-medium rounded-full hover:scale-105 transition-transform duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 transform-gpu"
                >
                  {isSubmitting ? (
                    <>
                      <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <span>Book Free Consultation</span>
                      <Icon name="ArrowRightIcon" size={18} />
                    </>
                  )}
                </button>

                <p className="text-xs text-white/40 text-center font-normal">
                 No spam. Your details are kept confidential.
                </p>
              </form>
            )}
          </div>
          </div>
        </div>
    </section>
  );
};

export default ConsultationCTA;